// WARNING: This DS is a weird combination of component and datasource, look
// elsewhere for code patterns to copy.
import { types as t } from '@invisr/mobx-state-tree'
import { makeObservable, action, observable, computed, runInAction, autorun, IObservableArray } from "mobx"
import crudActions from './crud-actions'
import Mapping from './mapping'
import React from 'react'
import ReactDOM from 'react-dom'
import { uistore } from '../uistore'
import getComponent from '../../utils/component-helper'
const Toaster = getComponent('Toaster')

const PROVIDER_TYPE = 'Toaster'

interface ToastsConfig {
	id: string
	styling: { mapper: any } // FIXME
	config: { mapper: any } // FIXME
}

export class ToastsProvider {
	_data = [] as IObservableArray
	config: ToastsConfig
	component
	upstreamDatasourceId = ''
	toastKey = 1
	mobxAutorun

	constructor(config: ToastsConfig) {
		makeObservable(this, {
			_data: observable,
			data: computed,
			upstreamDatasourceId: observable,
			mapper: computed,
			show: action,
			clear: action,
			close: action,
		})
		this.config = config
	}

	get mapper() {
		return this.config.config.mapper
	}

	get data() {
		if (this.upstreamDatasourceId) {
			const ds = uistore.ds(this.upstreamDatasourceId);
			const withIds = ds.data.map(v => {
				if (!v.id) {
					v = {
						id: this.toastKey++,
						...v
					}
				}
				return v
			})
			if (withIds.length) {
				runInAction(() => {
					withIds.forEach(x => this._data.push(x))
				})
			}
			ds.data.clear()
		}
		return this._data
	}

	close = (notification) => {
		this.data.remove(notification)
	}

	query = async () => {
		if (this.mobxAutorun) {
			return this.data
		}

		const elId = `root-${this.config.id}`
		let target = document.getElementById(elId)
		if (!target) {
			target = document.createElement('div')
			target.setAttribute('id', elId)
			document?.body.appendChild(target)
		}
		this.mobxAutorun = autorun(() => {
			const inputParams = this.config.config.mapper.toProps
			const styling = this.config.styling.mapper.toProps
			const { notificationsSource = [], ...toastProps} = inputParams
			const upDSId = this.config.config.mapper.mapping.asObject.notificationsSource?.dataSource?.id
			if (upDSId !== this.upstreamDatasourceId && (!!upDSId || !!this.upstreamDatasourceId)) {
				runInAction(() => this.upstreamDatasourceId = upDSId)
				this._data.concat(notificationsSource.map(v => {
					v.id = this.toastKey++;
					return v;
				}))
			}
			ReactDOM.render(<Toaster notificationsSource={this.data} onClose={this.close} styling={styling} {...toastProps} />, target)
		})
		return this.data
	}

	show = (message) => {
		const msg = typeof message === 'string' ? { id: this.toastKey++, message } : message
		if (!msg.id) {
			msg.id = this.toastKey++
		}
		this.data.unshift(msg)
	}
	clear = () => this.data.clear()
}

// const ICON_SIZE = ['lg', '2x', '3x', '4x', '5x']
const BORDER_STYLES = ['dotted', 'dashed', 'solid', 'none']
const TEXT_TRANSFORM = ['lowercase', 'uppercase', 'capitalize', 'none', 'inherit']
const FONT_WEIGHT = ['normal', 'bold', 'bolder']

const BUTTON_STYLES = [
	// {key: 'icon', type: 'icon'},
	// {key: 'iconSize', type: 'enum', meta: {picklist: ICON_SIZE}},
	{key: 'backgroundColor', type: 'color'},
	// {key: 'buttonHoverColor', type: 'color'},
	// {key: 'buttonDisabledColor', type: 'color'},
	{key: 'color', type: 'color'},
	// {key: 'labelHoverColor', type: 'color'},
	// {key: 'buttonHoverBorderColor', type: 'color'},
	// {key: 'buttonDisabledBorderColor', type: 'color'},
	{key: 'borderWidth', type: 'string'},
	{key: 'borderStyle', type: 'enum', meta: {picklist: BORDER_STYLES}},
	{key: 'borderRadius', type: 'string'},
	{key: 'padding', type: 'string'},
]

const ToastsConfig = t
	.model(`${PROVIDER_TYPE}Config`, {
		type: t.optional(t.string, PROVIDER_TYPE),
    config: t.maybeNull(t.late(() => Mapping)),
    styling: t.maybeNull(t.late(() => Mapping)),
	})
	.views(self => ({
		get mapping() {
			return self.config
		},
		labels() {
			return {
				key: (self as any).id as string,
				label: (self as any).name as string,
				childNodes: [
					{
						key: 'inputs', // this key determines the default params one of the methods below receives
						label: 'inputs',
						childNodes: [{key: 'message', type: 'string', bindingType: 'value', value: 'Simple Toast Message'}],
						isExpanded: false,
					},
					{
						key: 'methods', // these are shown as the autocomplete options when the DS is used in a fn mapping
						label: 'Methods',
						isExpanded: false,
						childNodes: [
							{
								key: 'show',
								label: 'show',
								secondaryLabel: 'FUNCTION'
							},
							{
								key: 'clear',
								label: 'clear',
								secondaryLabel: 'FUNCTION'
							},
						]
					}
				]
			}
		},
		get provider() {
			return ToastsProvider
		}
	}))
	.actions(crudActions)
	.actions(self => ({
		afterCreate () {
			if (!self.config) {
				self.update('config', {
					type: 'object',
					children: [{
						key: 'notificationsSource',
						type: 'Records',
						bindingType: 'data'
					},{
						key: 'position',
						type: 'enum',
						bindingType: 'value',
						value: 'top',
						meta: {
							picklist: ['top', 'bottom', 'top-right', 'bottom-right', 'top-left', 'bottom-left']
						}
					},{
						key: 'intent',
						type: 'enum',
						bindingType: 'value',
						value: 'none',
						meta: {
							picklist: ['none','info','success','warning','error']
						}
					},{
						key: 'timeout',
						type: 'number',
						bindingType: 'value',
						value: 3000
					},{
						key: 'onConfirmAction',
						type: 'func'
					},{
						key: 'onCancelAction',
						type: 'func'
					},{
						key: 'onBodyClick',
						type: 'func'
					},{
						key: 'confirmText',
						type: 'string',
						bindingType: 'value',
						value: 'Ok'
					},{
						key: 'cancelText',
						type: 'string',
						bindingType: 'value',
						value: 'Cancel'
					}]
				})
			}
			if (!self.styling) {
				self.update('styling', {
					type: 'object',
					children: [
						{
							key: 'background',
							type: 'object',
							children: [
								{key: 'default', type: 'color'},
								{key: 'success', type: 'color'},
								{key: 'error', type: 'color'},
								{key: 'warning', type: 'color'},
								{key: 'info', type: 'color'},
							]
						},
						{
							key: 'title',
							type: 'object',
							children: [
								{key: 'color', type: 'color'},
								{key: 'fontSize', type: 'string'},
								{key: 'fontWeight', type: 'enum', meta: {picklist: FONT_WEIGHT}},
								{key: 'textTransformation', type: 'enum', meta: {picklist: TEXT_TRANSFORM}},
								{key: 'textDecoration', type: 'string'}
							]
						},
						{
							key: 'message',
							type: 'object',
							children: [
								{key: 'color', type: 'color'},
								{key: 'fontSize', type: 'string'},
								{key: 'fontWeight', type: 'enum', meta: {picklist: FONT_WEIGHT}},
								{key: 'textTransformation', type: 'enum', meta: {picklist: TEXT_TRANSFORM}},
								{key: 'textDecoration', type: 'string'},
							]
						},
						{
							key: 'width',
							type: 'object',
							children: [
								{key: 'width', type: 'string'},
								{key: 'minWidth', type: 'string'},
								{key: 'maxWidth', type: 'string'},
							]
						},
						{
							key: 'border',
							type: 'object',
							children: [
								{key: 'borderColor', type: 'color'},
								{key: 'borderWidth', type: 'string'},
								{key: 'borderStyle', type: 'enum', meta: {picklist: BORDER_STYLES}},
								{key: 'borderRadius', type: 'string'},
							]
						},
						{
							key: 'confirmButton',
							type: 'object',
							children: BUTTON_STYLES
						},
						{
							key: 'cancelButton',
							type: 'object',
							children: BUTTON_STYLES
						}
					]
				})
			}
		}
	}))

	export default ToastsConfig
